import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const counters = [
  { label: 'Años de experiencia', value: 15, suffix: '+', icon: 'ri-award-line' },
  { label: 'Productos especializados', value: 30, suffix: '+', icon: 'ri-flask-line' },
  { label: 'Clientes en Venezuela', value: 80, suffix: '+', icon: 'ri-group-line' },
  { label: 'Proyectos completados', value: 200, suffix: '+', icon: 'ri-checkbox-circle-line' },
];

function useCounter(target: number, active: boolean, duration = 1800) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return count;
}

function CounterItem({ label, value, suffix, icon, active }: { label: string; value: number; suffix: string; icon: string; active: boolean }) {
  const count = useCounter(value, active);
  return (
    <div className="flex flex-col items-center text-center px-4 py-2">
      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/20 mb-3">
        <i className={`${icon} text-primary-400 text-xl`}></i>
      </div>
      <span className="font-heading font-bold text-4xl md:text-5xl text-white leading-none">
        {count}{suffix}
      </span>
      <span className="text-foreground-400 text-sm mt-2 leading-tight max-w-[120px]">{label}</span>
    </div>
  );
}

export default function ImpactBanner() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-24 md:py-32 overflow-hidden"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Venezuelan%20oil%20field%20at%20golden%20hour%20with%20pump%20jacks%20and%20industrial%20infrastructure%2C%20dramatic%20warm%20amber%20sky%2C%20silhouette%20of%20drilling%20equipment%2C%20cinematic%20wide%20angle%2C%20professional%20industrial%20photography%20with%20deep%20shadows%20and%20vivid%20orange%20tones%2C%20high%20contrast%20moody%20atmosphere&width=1800&height=700&seq=impact-banner-field-01&orientation=landscape"
          alt="Campo petrolero venezolano"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/80 to-foreground-950/60" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center">

          {/* Left: headline + CTA */}
          <div className={`flex-1 transition-all duration-800 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-1 h-10 rounded-full bg-accent-500" />
              <span className="text-accent-400 text-xs font-semibold tracking-widest uppercase">
                Química de alto rendimiento
              </span>
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-6xl text-white leading-tight mb-6">
              Soluciones que{' '}
              <span className="text-accent-400">impulsan</span>{' '}
              la producción
            </h2>
            <p className="text-foreground-300 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
              Poly Alphas Chemicals lleva más de 15 años desarrollando químicos especializados
              para la industria petrolera venezolana. Cada fórmula está diseñada para maximizar
              la producción y reducir los costos operativos.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/catalogo"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 hover:bg-primary-400 text-white font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                Ver Catálogo Completo
              </Link>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white hover:bg-white/10 font-semibold rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Contactar un Técnico
              </Link>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3 mt-8">
              {[
                { icon: 'ri-shield-check-line', text: 'Certificado PDVSA' },
                { icon: 'ri-test-tube-line', text: 'Laboratorio propio' },
                { icon: 'ri-map-pin-line', text: 'Atención nacional' },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15">
                  <i className={`${b.icon} text-accent-400 text-sm`}></i>
                  <span className="text-white/80 text-xs font-medium">{b.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right: counters */}
          <div className={`w-full lg:w-auto transition-all duration-800 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="grid grid-cols-2 gap-6 lg:gap-8">
              {counters.map((c) => (
                <CounterItem key={c.label} {...c} active={visible} />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}